import { showToast } from "./toastOptions";

/**
 * Error handling utilities for extracting error messages from backend responses
 * Backend returns errors in RFC 7807 Problem Details format:
 * {
 *   "title": "Error Title",
 *   "status": 400,
 *   "code": "ERROR_CODE",
 *   "detail": "Detailed error message",
 *   "type": "ExceptionType"
 * }
 */

/**
 * Extracts error message from backend response
 * @param {Response} response - Fetch response object
 * @returns {Promise<string>} Error message string
 */
export async function extractErrorMessage(response) {
  try {
    const contentType = response.headers.get("content-type");
    if (contentType?.includes("application/json")) {
      const errorData = await response.json();

      // Backend uses RFC 7807 Problem Details format
      if (errorData.detail) {
        return errorData.detail;
      }
      if (errorData.title) {
        return errorData.title;
      }
      if (errorData.message) {
        return errorData.message;
      }
      if (errorData.error) {
        return errorData.error;
      }

      // Fallback to status text if no message found
      return errorData.statusText || `Error ${errorData.status || response.status}`;
    }

    // If not JSON, try to get text
    const text = await response.text();
    if (text) {
      return text;
    }
  } catch (e) {
    // If parsing fails, fall back to status text
    showToast("error", "Failed to parse error response");
  }

  // Final fallback
  return response.statusText || `Error ${response.status}`;
}

/**
 * Handles HTTP error responses and throws an error with the backend message
 * @param {Response} response - Fetch response object
 * @param {string} defaultMessage - Default message if extraction fails
 * @throws {Error} Error with message from backend
 */
export async function handleHttpError(response, defaultMessage = "Request failed") {
  const message = await extractErrorMessage(response);
  const error = new Error(message || defaultMessage);
  error.status = response.status;
  error.response = response;
  throw error;
}
